import { graphql, useFragment } from 'react-relay';
import { useRelayFetchQuery } from '../relay.utils';
import type { User_Fragment$key } from './__generated__/User_Fragment.graphql';
import type { User_Search_Exists_Query } from './__generated__/User_Search_Exists_Query.graphql';
import type { Users_Pagination_Query } from './__generated__/Users_Pagination_Query.graphql';

export * from './__generated__/User_Fragment.graphql';
export const UserFragment = graphql`
  fragment User_Fragment on User {
    id
    slug
    role
    username
    name
    nicename
    avatar(size: 10) {
      url
    }
    meta {
      jobRole
      profileImage {
        id
        ...Media_Url_Fragment @relay(mask: false)
      }
      profileSocialLinks {
        name
        url
      }
      profileArticleLinks {
        name
        iconName
        url
      }
    }
    nickname
    firstName
    lastName
    description
    email
    capabilities
  }
`;

export const useUserFragment = (fragmentRef: User_Fragment$key) =>
  useFragment<User_Fragment$key>(UserFragment, fragmentRef);

// this framgent is used to list all users without exposing sensitive data
export const UsersFragment = graphql`
  fragment Users_Fragment on RootQuery {
    users(first: 100) {
      __id
      edges {
        node {
          id
          username
          name
          role
        }
      }
    }
  }
`;

export * from './__generated__/Users_Management_Query.graphql';
export const UsersManagementQuery = graphql`
  query Users_Management_Query($first: Int, $after: String, $where: RootQueryToUserConnectionWhereArgs) {
    ...Users_Management_Fragment
  }
`;

export const UserManagementFragment = graphql`
  fragment User_Management_Fragment on User {
    id
    username
    name
    role
    slug
    email
    nicename
    firstName
    lastName
    registeredDate
    avatar(size: 10) {
      url
    }
    meta {
      jobRole
      profileImage {
        id
        ...Media_Url_Fragment @relay(mask: false)
      }
      profileSocialLinks {
        name
        url
      }
      profileArticleLinks {
        name
        iconName
        url
      }
    }
    znglyVideos(first: 1, where: { stati: [PUBLISH, HIDDEN] }) {
      nodes {
        id
      }
    }
    forms(first: 1) {
      nodes {
        id
      }
    }
    znglists(first: 1, where: { stati: [PUBLISH, HIDDEN] }) {
      nodes {
        id
      }
    }
  }
`;

export * from './__generated__/Users_Management_Fragment.graphql';
export const UsersManagementFragment = graphql`
  fragment Users_Management_Fragment on RootQuery @refetchable(queryName: "UsersAdminFragment") {
    users(first: $first, after: $after, where: $where) @connection(key: "Users_admin_query_users") {
      __id
      edges {
        node {
          ...User_Management_Fragment @relay(mask: false)
        }
      }
    }
  }
`;

export type {
  User_Management_Single_Query,
  User_Management_Single_Query$data,
  User_Management_Single_Query$variables,
} from './__generated__/User_Management_Single_Query.graphql';
export const UserManagementSingleQuery = graphql`
  query User_Management_Single_Query($userId: ID!, $idType: UserNodeIdTypeEnum = ID) {
    user(id: $userId, idType: $idType) {
      ...User_Management_Fragment @relay(mask: false)
    }
  }
`;

export type {
  Users_Query,
  Users_Query$data,
  Users_Query$variables,
} from './__generated__/Users_Query.graphql';
export const UsersQuery = graphql`
  query Users_Query($first: Int = 24, $after: String, $where: RootQueryToUserConnectionWhereArgs) {
    ...Users_Pagination_Fragment 
      @arguments(
          first: $first
          after: $after
          where: $where
        )
  }
`;

export type Users_Pagination_Fragment$args = Users_Pagination_Query['variables'];

export * from './__generated__/Users_Pagination_Fragment.graphql';
export type {
  Users_Pagination_Query,
  Users_Pagination_Query$data,
  Users_Pagination_Query$variables,
} from './__generated__/Users_Pagination_Query.graphql';
export const UsersPaginationFragment = graphql`
  fragment Users_Pagination_Fragment on RootQuery 
  @argumentDefinitions(
    first: { type: "Int" }
    after: { type: "String" }
    where: { type: "RootQueryToUserConnectionWhereArgs" }
  )
  @refetchable(queryName: "Users_Pagination_Query") {
    users(first: $first, after: $after, where: $where) @connection(key: "Users_query_users") {
      __id
      edges {
        node {
          ...User_Fragment @relay(mask: false)
        }
      }
    }
  }
`;

export type {
  User_Single_Query,
  User_Single_Query$data,
  User_Single_Query$variables,
} from './__generated__/User_Single_Query.graphql';

export const UserSingleQuery = graphql`
  query User_Single_Query($id: ID!, $idType: UserNodeIdTypeEnum = ID) {
    user(id: $id, idType: $idType) {
      ...User_Fragment @relay(mask: false)
    }
  }
`;

export const UserSearchExistsQuery = graphql`
  query User_Search_Exists_Query($id: ID!, $idType: UserNodeIdTypeEnum = ID) {
    user(id: $id, idType: $idType) {
      id
    }
  }
`;

export const useUserSearchExistsQuery = () => useRelayFetchQuery<User_Search_Exists_Query>(UserSearchExistsQuery);
